import { initializeApp } from 'firebase/app'
import {
  getDatabase,
  ref,
  update,
  push,
  set,
  remove,
  get,
  runTransaction,
  onValue,
  onChildAdded,
  onChildChanged,
  onChildMoved,
  onChildRemoved,
  query,
  orderByKey,
  orderByValue,
  orderByChild,
  limitToFirst,
  limitToLast,
  startAt,
  startAfter,
  endAt,
  endBefore,
  equalTo
} from 'firebase/database'
import {
  getFirestore,
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  addDoc,
  onSnapshot,
  query as firestoreQuery,
  where,
  deleteField,
  orderBy,
  limit,
  getCountFromServer,
  startAfter as firestoreStartAfter,
  endBefore as firestoreEndBefore,
  limitToLast as firestoreLimitToLast
} from 'firebase/firestore'
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions'
import CommonGlobalConfig from './CommonGlobalConfig'
import EventEmitter from 'events'

const GlobalConfig = (function () {
  let firebaseConfig = {
    apiKey: process.env.REACT_APP_API_KEY,
    authDomain: process.env.REACT_APP_AUTH_DOMAIN,
    databaseURL: process.env.REACT_APP_DATABASE_URL,
    storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
    projectId: process.env.REACT_APP_PROJECT_ID,
    appId: process.env.REACT_APP_APP_ID
  }

  console.log('Firebase Config', firebaseConfig)

  // Initialize Firebase
  const firebaseApp = initializeApp(firebaseConfig)
  const realtimeDb = getDatabase(firebaseApp)
  const firestoreDb = getFirestore(firebaseApp)

  // Use the firebase functions running locally during development
  // Comment if you want to run the hosted version
  const functions = getFunctions(firebaseApp)
  if (__DEV__) {
    connectFunctionsEmulator(functions, 'localhost', 5001)
  }

  const eventEmitter = new EventEmitter()

  // Helper functions for modular Firebase API
  const firebaseHelpers = {
    createRef: path => ref(realtimeDb, path),
    updateData: (reference, updates) => update(reference, updates),
    setData: (reference, data) => set(reference, data),
    removeData: reference => remove(reference),
    getData: reference => get(reference),
    transaction: (reference, updateFunction) =>
      runTransaction(reference, updateFunction),
    pushData: (reference, data) => push(reference, data),
    generatePushKey: reference => push(reference).key,
    // Query helpers
    createQuery: (reference, ...constraints) =>
      query(reference, ...constraints),
    orderByKey: () => orderByKey(),
    orderByValue: () => orderByValue(),
    orderByChild: childPath => orderByChild(childPath),
    limitToFirst: limit => limitToFirst(limit),
    limitToLast: limit => limitToLast(limit),
    startAt: value => startAt(value),
    startAfter: value => startAfter(value),
    endAt: value => endAt(value),
    endBefore: value => endBefore(value),
    equalTo: value => equalTo(value),
    // Event listener helpers
    onValue: (reference, callback, errorCallback) =>
      onValue(reference, callback, errorCallback),
    onChildAdded: (reference, callback, errorCallback) =>
      onChildAdded(reference, callback, errorCallback),
    onChildChanged: (reference, callback, errorCallback) =>
      onChildChanged(reference, callback, errorCallback),
    onChildMoved: (reference, callback, errorCallback) =>
      onChildMoved(reference, callback, errorCallback),
    onChildRemoved: (reference, callback, errorCallback) =>
      onChildRemoved(reference, callback, errorCallback),
    // Firestore helpers
    collectionRef: collectionPath => collection(firestoreDb, collectionPath),
    docRef: (collectionRef, docId) =>
      docId ? doc(collectionRef, docId) : doc(collectionRef),
    getDocument: docRef => getDoc(docRef),
    setDocument: (docRef, data) => setDoc(docRef, data),
    updateDocument: (docRef, data) => updateDoc(docRef, data),
    deleteDocument: docRef => deleteDoc(docRef),
    addDocument: (collectionRef, data) => addDoc(collectionRef, data),
    onSnapshot: (reference, callback, errorCallback) =>
      onSnapshot(reference, callback, errorCallback),
    // Firestore query helpers
    createFirestoreQuery: (reference, ...constraints) =>
      firestoreQuery(reference, ...constraints),
    where: (field, opStr, value) => where(field, opStr, value),
    orderBy: (field, direction) => orderBy(field, direction),
    limit: count => limit(count),
    getCountFromServer: reference => getCountFromServer(reference),
    firestoreStartAfter: value => firestoreStartAfter(value),
    firestoreEndBefore: value => firestoreEndBefore(value),
    firestoreLimitToLast: count => firestoreLimitToLast(count)
  }

  // To create a test release, set test to !__DEV__ and testUser to 'vg'
  return {
    ...CommonGlobalConfig,
    maxParticipantsToShowInAlarmSummary: 8,
    rootFirebaseRef: ref(realtimeDb),
    realtimeDb,
    firebaseHelpers,
    firestoreDeleteField: deleteField,
    dynamicLinkDomain: process.env.REACT_APP_DYNAMIC_LINK_DOMAIN,
    eventEmitter: eventEmitter,
    reducerVersion: '1.0',
    firebaseApiKey: firebaseConfig.apiKey,
    eventsThresholdForCalendarView: {
      month: 3,
      week: 8,
      day: 8
    },
    firestoreDb,
    webColorScheme: CommonGlobalConfig.defaultColorScheme,
    primaryColorId: CommonGlobalConfig.defaultPrimaryColorId
  }
})()

export default GlobalConfig
